import { useEffect, useState } from "react";
import {
	Box,
	Button,
	Chip,
	CircularProgress,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	TextField,
	Typography,
	useTheme,
} from "@mui/material";
import { WarningAmber } from "@mui/icons-material";
import useStyles from "./styles";
import api from "../../../services/api";
import corNeutraChip from "../corNeutraChip";

// Palavra que precisa ser digitada, exatamente, pra habilitar o botao de confirmacao -
// comparacao case-sensitive, sem trim: e a fricao extra desta acao, a mais perigosa da feature
const PALAVRA_CONFIRMACAO = "FORÇAR";

export default function ModalForcarAtualizacao({
	open,
	onClose,
	clienteId,
	chave,
	nomeCliente,
	onConcluido,
}) {
	const [carregandoDivergencia, setCarregandoDivergencia] = useState(true);
	const [divergencia, setDivergencia] = useState(null);
	const [erroDivergencia, setErroDivergencia] = useState(null);
	const [textoConfirmacao, setTextoConfirmacao] = useState("");
	const [enviando, setEnviando] = useState(false);
	const [resultado, setResultado] = useState(null);
	const [erroEnvio, setErroEnvio] = useState(null);
	const theme = useTheme();
	const styles = useStyles(theme);

	// Busca a divergencia atual toda vez que o modal abre - mesma chamada do ModalDivergencia,
	// mas fluxo de confirmacao distinto (aqui a falha na busca nao bloqueia o botao de forcar)
	useEffect(() => {
		if (!open) return;
		buscarDivergencia();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [open, clienteId, chave]);

	// GET /versionamento/templates/divergencia - se falhar (rede/API do Zabbix), so avisa;
	// quem decide bloquear ou nao a checagem em si e o backend, este modal so informa
	async function buscarDivergencia() {
		setCarregandoDivergencia(true);
		setDivergencia(null);
		setErroDivergencia(null);
		setTextoConfirmacao("");
		setEnviando(false);
		setResultado(null);
		setErroEnvio(null);
		try {
			const response = await api.get("/versionamento/templates/divergencia", {
				params: { clienteId, chave },
			});
			setDivergencia(response?.data || null);
		} catch (error) {
			setErroDivergencia(
				error?.response?.data?.error ||
					"Não foi possível verificar a divergência atual deste cliente.",
			);
		} finally {
			setCarregandoDivergencia(false);
		}
	}

	// POST /versionamento/templates/forcar - sempre ignorarDivergencia:true no backend, sempre 1 cliente
	// por chamada. Erro de rede/500 aqui e generico, mesmo padrao dos outros modais desta feature
	async function handleForcarAtualizacao() {
		setEnviando(true);
		setErroEnvio(null);
		try {
			const response = await api.post("/versionamento/templates/forcar", {
				clienteId,
				chave,
			});
			setResultado(response?.data || null);
		} catch (error) {
			setErroEnvio(
				error?.response?.data?.error ||
					"Não foi possível enviar a atualização forçada. Tente novamente.",
			);
		} finally {
			setEnviando(false);
		}
	}

	// Fecha o modal - se ja havia resultado (forcar enviado), dispara o refresh do pai antes
	function handleFechar() {
		if (enviando) return;
		if (resultado) {
			onConcluido?.();
		}
		onClose?.();
	}

	// Par fundo/texto do status de cada linha do resumo - mesmos tokens de chip.background/colorText
	// ja usados no resto da feature, com corNeutraChip pro caso sem status forte
	function coresResultado(status) {
		if (status === "sucesso") {
			return {
				background: theme.palette.chip.background.success,
				color: theme.palette.chip.colorText.success,
			};
		}
		if (status === "falha") {
			return {
				background: theme.palette.chip.background.critical,
				color: theme.palette.chip.colorText.critical,
			};
		}
		return corNeutraChip(theme);
	}

	const temExtras = (divergencia?.extrasNoCliente?.length || 0) > 0;
	const resumo = resultado?.resumo || [];
	const confirmacaoHabilitada = textoConfirmacao === PALAVRA_CONFIRMACAO;

	return (
		<Dialog open={open} onClose={handleFechar} fullWidth maxWidth="sm">
			<DialogTitle sx={styles.tituloSevero}>
				<WarningAmber fontSize="small" />
				Forçar atualização — ação de risco
			</DialogTitle>
			<DialogContent>
				<Typography variant="body2" sx={styles.subtitulo}>
					{nomeCliente} — {chave}
				</Typography>
				{carregandoDivergencia && (
					<Box sx={styles.loadingContainer}>
						<CircularProgress size={28} />
					</Box>
				)}
				{!carregandoDivergencia && resultado === null && (
					<Box>
						<Typography variant="body2" sx={styles.textoPrincipal}>
							Isso vai forçar a atualização de <strong>{chave}</strong> no cliente{" "}
							<strong>{nomeCliente}</strong>, ignorando qualquer divergência.
						</Typography>
						{erroDivergencia && (
							<Typography variant="body2" sx={styles.avisoDivergenciaIndisponivel}>
								{erroDivergencia} Você ainda pode decidir forçar a atualização mesmo
								sem essa checagem.
							</Typography>
						)}
						{!erroDivergencia && (
							<Box sx={temExtras ? styles.blocoAlerta : undefined}>
								{temExtras ? (
									<>
										<Typography variant="subtitle2" sx={styles.textoAlerta}>
											Itens exclusivos do cliente
										</Typography>
										<Typography variant="body2" sx={styles.textoAlerta}>
											Estes itens são customização local e podem ser sobrescritos
											ou entrar em conflito com a atualização forçada.
										</Typography>
										<Box component="ul" sx={styles.lista}>
											{divergencia.extrasNoCliente.map((itemExtra) => (
												<Box component="li" key={itemExtra} sx={styles.itemListaAlerta}>
													{itemExtra}
												</Box>
											))}
										</Box>
									</>
								) : (
									<Typography variant="body2" sx={styles.semItens}>
										Nenhum item exclusivo do cliente identificado na divergência
										atual — o evento anterior pode ter falhado por outro motivo.
									</Typography>
								)}
							</Box>
						)}
						<Box sx={styles.confirmacaoContainer}>
							<Typography variant="body2" sx={styles.textoConfirmacao}>
								Para confirmar, digite {PALAVRA_CONFIRMACAO} no campo abaixo.
							</Typography>
							<TextField
								size="small"
								value={textoConfirmacao}
								disabled={enviando}
								onChange={(event) => setTextoConfirmacao(event.target.value)}
								placeholder={PALAVRA_CONFIRMACAO}
							/>
						</Box>
						{erroEnvio && (
							<Typography variant="body2" sx={styles.erro}>
								{erroEnvio}
							</Typography>
						)}
					</Box>
				)}
				{!carregandoDivergencia && resultado !== null && (
					<Box sx={styles.resultadoContainer}>
						{resumo.length === 0 && (
							<Typography variant="body2" sx={styles.semItens}>
								Nenhum resultado retornado.
							</Typography>
						)}
						{resumo.map((linha) => {
							const cor = coresResultado(linha?.status);
							return (
								<Box key={linha?.chave} sx={styles.linhaResultado}>
									<Chip
										size="small"
										label={linha?.status}
										sx={{
											...styles.chip,
											backgroundColor: cor.background,
											color: cor.color,
										}}
									/>
									{linha?.status !== "sucesso" && linha?.motivo && (
										<Typography variant="caption" sx={styles.motivoResultado}>
											{linha.motivo}
										</Typography>
									)}
									{linha?.versaoFinal && (
										<Typography variant="caption" sx={styles.versaoResultado}>
											Versão final: {linha.versaoFinal}
										</Typography>
									)}
								</Box>
							);
						})}
					</Box>
				)}
			</DialogContent>
			<DialogActions>
				{resultado === null ? (
					<>
						<Button onClick={handleFechar} disabled={enviando}>
							Cancelar
						</Button>
						<Button
							variant="contained"
							sx={styles.botaoForcar}
							disabled={enviando || carregandoDivergencia || !confirmacaoHabilitada}
							onClick={handleForcarAtualizacao}
						>
							{enviando ? <CircularProgress size={20} /> : "Forçar atualização"}
						</Button>
					</>
				) : (
					<Button variant="contained" onClick={handleFechar}>
						Fechar
					</Button>
				)}
			</DialogActions>
		</Dialog>
	);
}
